import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import FilterBar from "../components/FilterBar";
import DealCard from "../components/DealCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1800&q=85&auto=format&fit=crop";

const LOAD_MORE = 6;

const getInitialPageSize = () => {
  if (typeof window === "undefined") return 12;
  if (window.innerWidth < 640) return 6;
  if (window.innerWidth < 1024) return 8;
  return 12;
};

/** Pretty-print a slug like "new-york" → "New York" */
const prettifyCity = (slug) =>
  decodeURIComponent(slug || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function CityDealsPage() {
  const { city } = useParams();
  const cityDisplay = prettifyCity(city);

  const [searchParams] = useSearchParams();

  // Pre-populate filters from URL params (?region=Europe, ?type=flight)
  const [filters, setFilters] = useState(() => {
    const init = {};
    const region = searchParams.get("region");
    const type   = searchParams.get("type");
    if (region) init.region    = region;
    if (type)   init.deal_type = type;
    return init;
  });
  const [pageSize] = useState(getInitialPageSize);
  const [deals, setDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [clock, setClock] = useState("");

  const dealsThisWeek = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return deals.filter((d) => new Date(d.created_at).getTime() >= cutoff).length;
  }, [deals]);

  const buildParams = useCallback((extraSkip = 0, limit = pageSize) => ({
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null)),
    from_city: cityDisplay,
    limit,
    skip: extraSkip,
  }), [filters, pageSize, cityDisplay]);

  // Scroll to feed when arriving with a filter param
  useEffect(() => {
    if (searchParams.get("region") || searchParams.get("type")) {
      setTimeout(() => {
        document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tick = () => {
      const time = new Date().toLocaleTimeString("en-GB", { timeZone: "Europe/Amsterdam", hour: "2-digit", minute: "2-digit" });
      setClock(`${time} CET`);
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get("/deals", { params: buildParams(0) })
        .then((r) => {
          setDeals(r.data.items);
          setTotal(r.data.total);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters, buildParams]);

  const loadMore = () => {
    setLoadingMore(true);
    api
      .get("/deals", { params: buildParams(deals.length, LOAD_MORE) })
      .then((r) => {
        setDeals((prev) => [...prev, ...r.data.items]);
      })
      .finally(() => setLoadingMore(false));
  };

  const hasMore = deals.length < total;

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Helmet>
        <title>Cheap Flights from {cityDisplay} — Restricted Deals</title>
        <link rel="canonical" href={`https://restricteddeals.com/from/${encodeURIComponent(city)}`} />
        <meta name="description" content={`The latest flight, hotel and travel deals departing from ${cityDisplay}. Updated daily.`} />
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#2A2A2A] scanlines" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="flight tracker" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/85 to-[#111111]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-36 text-white">
          <div className="flex items-center gap-4 mb-6 text-[11px] font-mono tracking-[0.22em] uppercase text-[#9A8F83]">
            <span className="inline-block w-2 h-2 bg-[#CC1A1A] animate-pulse" />
            <span>LIVE · FEED {clock}</span>
            <span className="text-[#3A3A3A]">│</span>
            <span>DISPATCH {String(total).padStart(2, "0")}</span>
          </div>

          <p className="eyebrow eyebrow-red mb-4">// RESTRICTED — DO NOT FORWARD</p>

          <h1 className="font-mono text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-[-0.035em] max-w-5xl uppercase text-white">
            Deals from <span className="text-[#CC1A1A]">{cityDisplay}</span>
          </h1>

          <p className="mt-6 text-white font-sans text-base leading-snug">
            Free deal alerts — flights, hotels, cruises &amp; packages from {cityDisplay}.
          </p>

          <p className="mt-4 max-w-xl text-[#BEB3A6] text-lg leading-relaxed font-sans">
            All the best deals departing from {cityDisplay}, updated as they drop.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#feed" className="btn-rd" data-testid="hero-cta-browse">
              SEE LATEST DEALS ↓
            </a>
            <a href="#newsletter" className="btn-rd-outline" data-testid="hero-cta-subscribe">
              GET FREE DEAL ALERTS →
            </a>
          </div>

          {/* Departure-board ticker stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2A2A] max-w-3xl border border-[#2A2A2A]">
            <Stat label="LIVE FILES" value={total} />
            <Stat label="REGIONS" value="6" />
            <Stat label="THIS WEEK" value={dealsThisWeek} />
            <Stat label="CLASSIFIED" value="YES" mono />
          </div>
        </div>
      </section>

      <FilterBar filters={filters} setFilters={setFilters} />

      {/* Feed */}
      <main id="feed" className="flex-1 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="eyebrow eyebrow-red">// DEALS FROM {cityDisplay.toUpperCase()}</p>
              <h2 className="font-mono text-3xl md:text-5xl tracking-tight mt-2 text-white uppercase">
                Fresh intel from {cityDisplay}
              </h2>
            </div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#9A8F83]">
              {total} file{total === 1 ? "" : "s"} · updated {clock}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="deals-loading">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-[#1A1A1A]" />
                  <div className="h-3 w-24 bg-[#1A1A1A] mt-4" />
                  <div className="h-6 w-48 bg-[#1A1A1A] mt-3" />
                </div>
              ))}
            </div>
          ) : deals.length === 0 ? (
            <div className="py-24 text-center border border-[#2A2A2A] rd-card" data-testid="deals-empty">
              <p className="eyebrow eyebrow-red">// NO ACTIVE FILES</p>
              <h3 className="font-mono text-3xl mt-3 text-white uppercase">Channel quiet for {cityDisplay}.</h3>
              <p className="text-[#9A8F83] mt-3">No deals from {cityDisplay} right now. Check back soon or adjust filters above.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12" data-testid="deals-grid">
                {deals.map((d, i) => (
                  <DealCard key={d.id} deal={d} index={i} />
                ))}
              </div>

              {/* Pagination controls */}
              {(hasMore || total > pageSize) && (
                <div className="mt-16 flex flex-col items-center gap-4">
                  {hasMore ? (
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="btn-rd-outline !px-8 !py-3 disabled:opacity-50"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? "// LOADING…" : "// LOAD MORE FILES →"}
                    </button>
                  ) : (
                    <p className="text-[11px] font-mono tracking-[0.18em] uppercase text-[#444444]">
                      // ALL FILES LOADED — {total} deal{total === 1 ? "" : "s"}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Trip.com Flight Search Box — below feed */}
      <div className="bg-[#111111] px-4 sm:px-6 lg:px-10 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="hidden md:block" style={{ background: '#0B0B0B', border: '1px solid #2A2A2A', padding: '24px' }}>
            <p style={{ fontFamily: 'monospace', fontSize: '11px', color: '#CC1A1A', letterSpacing: '0.2em', marginBottom: '16px' }}>// SEARCH & BOOK</p>
            <iframe
              src="https://www.trip.com/partners/ad/S16678384?Allianceid=4012514&SID=308550495&trip_sub1="
              style={{ width: '100%', height: '198px', border: 'none', display: 'block' }}
              frameBorder="0"
              scrolling="no"
              title="Trip.com Flight Search"
            />
            <p style={{ fontFamily: 'monospace', fontSize: '10px', color: '#333', marginTop: '8px' }}>Powered by Trip.com · affiliate partner</p>
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="bg-[#0E0E0E] px-5 py-4">
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#9A8F83]">{label}</p>
      <p className={`mt-1 font-mono text-2xl ${mono ? "text-[#CC1A1A]" : "text-white"} tracking-tight`}>{value}</p>
    </div>
  );
}
